import { clearIntegrationData } from '@/features/cyclr/dataSourceIntegrationSlice';
import { DataSourceTypePanelV2 } from '@/features/dataSources/components';
import { DataSourceTypeCard } from '@/features/dataSources/components/commons';
import { CreateDataSource } from '@/features/dataSources/components/createDataSource';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { ConnectorInReviewModal } from '@/features/destinations/commons';
import { resetUploadStatus } from '@/features/fileUploading/fileUploadingSlice';
import { SupportModal } from '@/features/layout/Sidebars/common/components/SidebarBase/SidebarNavigation/Items/SidebarSupportItem/SupportModal';
import { clearStashedRules } from '@/features/rules/rulesSlice';
import { SupportSuccessModal } from '@/features/support';
import { useModalContext } from '@/hooks/useModalContext';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { DataSourceIDEnum, DataSourceType } from '@/models/dataSource';
import { EStatus } from '@/models/fileUpload';
import { SLIDEOVER_TRANSITION_DURATION } from '@/providers';
import { RootState, useAppDispatch } from '@/reducers';
import { delay } from 'lodash';
import { FC, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CreateCampaignFormInput } from '../../../../validation';
import { useCampaignsActions } from '../../../../context';

type DataSourceItemProps = {
  className?: string;
  dataSource: DataSourceType;
};

export const DataSourceItem: FC<DataSourceItemProps> = ({ dataSource, className }) => {
  const { control } = useFormContext<CreateCampaignFormInput>();
  const { append: addCreatedDataSource } = useFieldArray({
    control,
    name: 'createdDataSources',
  });

  const fileUploadStatus = useSelector((state: RootState) => state.fileUploading.status);
  const dispatch = useAppDispatch();
  const { openSlideOver, closeSlideOver } = useSlideOverContext();
  const { openModal, closeModal: closeModalContext } = useModalContext();
  const { setNextStep } = useCampaignsActions();

  const [showedModal, setShowedModal] = useState<'default' | 'connectorInReview' | 'supportSuccess' | null>(null);

  const closeModal = () => setShowedModal(null);
  const showSupportModal = () => setShowedModal('default');
  const showConnectorInReviewModal = () => setShowedModal('connectorInReview');
  const showSupportSuccessModal = () => setShowedModal('supportSuccess');

  const isSupportModalVisible = showedModal === 'default';
  const isConnectorInReviewModalVisible = showedModal === 'connectorInReview';
  const isSupportSuccessModalVisible = showedModal === 'supportSuccess';

  const onAddDataSourceClicked = () => {
    if (fileUploadStatus?.uploadingStatus !== EStatus.UPLOADING) {
      dispatch(resetUploadStatus());
    }

    dispatch(clearStashedDataSource());
    dispatch(clearStashedRules());
    dispatch(clearIntegrationData());

    closeSlideOver();
    delay(() => {
      openModal({
        renderContent: CreateDataSource,
        renderContentProps: {
          dataSourceId: dataSource.id as DataSourceIDEnum,
          onSuccess: (dataSource) => {
            addCreatedDataSource(dataSource);
          },
          onSuccessAndContinue: (dataSource) => {
            addCreatedDataSource(dataSource);
            setTimeout(() => {
              setNextStep();
              closeModalContext();
            }, 0);
          },
        },
        fullHeight: true,
        fullWidth: true,
      });
    }, SLIDEOVER_TRANSITION_DURATION);
  };

  const handleClick = (dataSourceType: DataSourceType) => {
    if (dataSourceType.name === 'Salesforce') {
      showConnectorInReviewModal();
      return;
    }
    openSlideOver({
      renderContent: DataSourceTypePanelV2,
      renderContentProps: { dataSourceType, onAddDataSourceClicked: onAddDataSourceClicked },
      showButtonInsideSlideOver: true,
    });
  };

  return (
    <>
      <DataSourceTypeCard key={dataSource.name} dataSourceType={dataSource} className={className} onClick={() => handleClick(dataSource)} />
      {isConnectorInReviewModalVisible && <ConnectorInReviewModal onConfirm={async () => showSupportModal()} onClose={closeModal} />}
      {isSupportModalVisible && <SupportModal onConfirm={showSupportSuccessModal} onClose={closeModal} />}
      {isSupportSuccessModalVisible && <SupportSuccessModal onConfirm={async () => closeModal()} onClose={closeModal} />}
    </>
  );
};
